export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'RecyclerBin';
export const FRONTEND_BASE_URL = import.meta.env.VITE_FRONTEND_BASE_URL || window.location.origin;
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

export const ROUTES = {
  HOME: '/',
  LOGIN: '/auth/login',
  SIGNUP: '/auth/signup',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  PRODUCTS: '/products',
  PRODUCT_DETAIL: '/products/:id',
  SHOP: '/shop/:slug',
  SHOP_BY_ID: '/shop/id/:id',
  ABOUT: '/about',
  CONTACT: '/contact',
  
  // Buyer routes
  BUYER_DASHBOARD: '/buyer',
  CART: '/dashboard/buyer/cart', // Direct route to avoid redirect issues
  CHECKOUT: '/dashboard/buyer/checkout',
  ORDERS: '/dashboard/buyer/orders',
  WISHLIST: '/dashboard/buyer/wishlist',
  
  // Seller routes
  SELLER_DASHBOARD: '/dashboard/seller',
  SHOP_MANAGEMENT: '/dashboard/seller/shop',
  PRODUCT_MANAGEMENT: '/dashboard/seller/products',
  ADD_PRODUCT: '/dashboard/seller/products/add',
  EDIT_PRODUCT: '/dashboard/seller/products/edit/:id',
  INVENTORY: '/dashboard/seller/inventory',
  SELLER_ORDERS: '/dashboard/seller/orders',
  SELLER_ANALYTICS: '/dashboard/seller/analytics',
  COMMISSION: '/dashboard/seller/commission',
  
  // Admin routes
  ADMIN_DASHBOARD: '/admin',
  USER_MANAGEMENT: '/admin/users',
  SHOP_MANAGEMENT_ADMIN: '/admin/shops',
  PRODUCT_MODERATION: '/admin/products',
  ORDER_MANAGEMENT: '/admin/orders',
  ADMIN_ANALYTICS: '/admin/analytics',
  SYSTEM_SETTINGS: '/admin/settings',
  
  // Staff routes
  STAFF_DASHBOARD: '/staff',
  STAFF_PRODUCTS: '/staff/products',
  STAFF_ORDERS: '/staff/orders',
  
  // Super Admin routes
  SUPER_ADMIN_DASHBOARD: '/super-admin',
} as const;

export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  STAFF: 'staff',
  USER: 'user',
} as const;

export const ROLE_ID_MAPPING = {
  1: USER_ROLES.SUPER_ADMIN,
  2: USER_ROLES.ADMIN,
  3: USER_ROLES.STAFF,
  4: USER_ROLES.USER,
} as const;

export const ORDER_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
} as const;

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const;

export const PRODUCT_TYPES = {
  BRAND_NEW: 'brand_new',
  RECYCLED: 'recycled',
} as const;

export const SHIPPING_METHODS = {
  CASH_ON_DELIVERY: 'cash_on_delivery',
  ADVANCE_PAYMENT: 'advance_payment',
  STANDARD: 'standard',
} as const;

export const ORDER_STATUS_COLORS = {
  [ORDER_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800',
  [ORDER_STATUS.CONFIRMED]: 'bg-blue-100 text-blue-800',
  [ORDER_STATUS.SHIPPED]: 'bg-purple-100 text-purple-800',
  [ORDER_STATUS.DELIVERED]: 'bg-green-100 text-green-800',
  [ORDER_STATUS.CANCELLED]: 'bg-red-100 text-red-800',
} as const;

export const PAYMENT_STATUS_COLORS = {
  [PAYMENT_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800',
  [PAYMENT_STATUS.PAID]: 'bg-green-100 text-green-800',
  [PAYMENT_STATUS.FAILED]: 'bg-red-100 text-red-800',
  [PAYMENT_STATUS.REFUNDED]: 'bg-gray-100 text-gray-800',
} as const;

export const PRODUCT_TYPE_LABELS = {
  [PRODUCT_TYPES.BRAND_NEW]: 'Brand New',
  [PRODUCT_TYPES.RECYCLED]: 'Recycled',
} as const;

export const SHIPPING_METHOD_LABELS = {
  [SHIPPING_METHODS.CASH_ON_DELIVERY]: 'Cash on Delivery',
  [SHIPPING_METHODS.ADVANCE_PAYMENT]: 'Advance Payment',
  [SHIPPING_METHODS.STANDARD]: 'Standard Shipping',
} as const;

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const;

export const FILE_UPLOAD = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  MAX_IMAGES_PER_PRODUCT: 10,
} as const;

export const COMMISSION = {
  DEFAULT_RATE: 0.05, // 5%
  MIN_COMMISSION: 10.00,
  MAX_COMMISSION: 100.00,
} as const;

export const INVENTORY = {
  LOW_STOCK_THRESHOLD: 10,
  OUT_OF_STOCK_THRESHOLD: 0,
} as const;

// Standard product condition values
export const PRODUCT_CONDITIONS = [
  'Brand New',
  'Like New',
  'Excellent',
  'Good',
  'Fair',
  'Refurbished',
  'For Parts',
] as const;

export type ProductCondition = typeof PRODUCT_CONDITIONS[number];